/**
 * LOAScanPage - Hidden admin page for bulk LOA email precheck scanning.
 *
 * Fires off LLMBlock workflows for all open LOR Acknowledgement tasks,
 * shows live results as workflows complete, and allows applying task updates.
 */

import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Filter,
  Loader2,
  Play,
  RefreshCw,
  Save,
  XCircle,
} from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import type { LOAScanResultItem } from '@/services/api/agentflowService';
import {
  useApplyLOAScanUpdatesMutation,
  useGetLOAScanLatestQuery,
  useLazyGetLOAScanResultsQuery,
  useStartLOAScanMutation,
} from '@/services/api/agentflowService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute a human-readable age string from an ISO date string. */
function formatAge(isoDate: string | null): string {
  if (!isoDate) return '-';
  const created = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  if (diffMs < 0) return '-';

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days >= 365) {
    const years = Math.floor(days / 365);
    return `${years}y`;
  }
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months}mo`;
  }
  if (days >= 1) return `${days}d`;

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours >= 1) return `${hours}h`;

  const minutes = Math.floor(diffMs / (1000 * 60));
  return `${Math.max(1, minutes)}m`;
}

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

const ScanStatusBadge: React.FC<{ status: LOAScanResultItem['status'] }> = ({
  status,
}) => {
  switch (status) {
    case 'completed':
      return (
        <Badge color="green" className="gap-1">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </Badge>
      );
    case 'running':
      return (
        <Badge color="blue" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Running
        </Badge>
      );
    case 'failed':
      return (
        <Badge color="red" className="gap-1">
          <XCircle className="h-3 w-3" />
          Failed
        </Badge>
      );
    default:
      return (
        <Badge color="zinc" className="gap-1">
          Not Found
        </Badge>
      );
  }
};

const ActionBadge: React.FC<{ action: string | null }> = ({ action }) => {
  if (!action) return null;
  if (action === 'send') {
    return <Badge color="green">SEND</Badge>;
  }
  if (action === 'stop') {
    return <Badge color="red">STOP</Badge>;
  }
  return <Badge color="zinc">{action}</Badge>;
};

// ---------------------------------------------------------------------------
// Filter types
// ---------------------------------------------------------------------------

type StatusFilterValue = LOAScanResultItem['status'];
type ActionFilterValue = 'send' | 'stop';

const ALL_STATUS_VALUES: StatusFilterValue[] = [
  'running',
  'completed',
  'failed',
  'not_found',
];
const ALL_ACTION_VALUES: ActionFilterValue[] = ['send', 'stop'];

const STATUS_ACTIVE_CLASSES: Record<StatusFilterValue, string> = {
  completed:
    'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  running:
    'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  not_found:
    'bg-zinc-100 text-zinc-800 dark:bg-zinc-900/30 dark:text-zinc-400',
};
const INACTIVE_CLASS =
  'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500';

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export const LOAScanPage: React.FC = () => {
  const [scanTs, setScanTs] = React.useState<number | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = React.useState<Set<string>>(
    new Set(),
  );
  const [appliedTaskIds, setAppliedTaskIds] = React.useState<Set<string>>(
    new Set(),
  );
  const pollingRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const [scanUnavailable, setScanUnavailable] = React.useState(false);
  const consecutiveErrorsRef = React.useRef(0);
  const MAX_CONSECUTIVE_ERRORS = 3;

  // Filter state
  const [statusFilters, setStatusFilters] = React.useState<
    Set<StatusFilterValue>
  >(new Set(ALL_STATUS_VALUES));
  const [actionFilters, setActionFilters] = React.useState<
    Set<ActionFilterValue>
  >(new Set(ALL_ACTION_VALUES));

  const [startScan, { isLoading: isStarting }] = useStartLOAScanMutation();
  const [
    fetchResults,
    { data: resultsData, isFetching: isFetchingResults },
  ] = useLazyGetLOAScanResultsQuery();
  const [applyUpdates, { isLoading: isApplying }] =
    useApplyLOAScanUpdatesMutation();

  // Fetch latest scan on mount to restore in-flight runs after refresh
  const { data: latestData } = useGetLOAScanLatestQuery();
  React.useEffect(() => {
    if (latestData?.scan_ts && !scanTs) {
      setScanTs(latestData.scan_ts);
    }
  }, [latestData, scanTs]);

  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Start polling when scanTs changes
  React.useEffect(() => {
    if (!scanTs) return undefined;

    // Reset state for new scan
    setScanUnavailable(false);
    consecutiveErrorsRef.current = 0;

    // Initial fetch
    fetchResults({ scanTs });

    // Poll every 1 second
    pollingRef.current = setInterval(() => {
      fetchResults({ scanTs })
        .unwrap()
        .then((data) => {
          consecutiveErrorsRef.current = 0;
          // Stop polling if the workflow has been garbage-collected
          if (data.scan_unavailable) {
            setScanUnavailable(true);
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
          }
        })
        .catch(() => {
          consecutiveErrorsRef.current += 1;
          if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
            // Stop polling after repeated failures to avoid hammering the server
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setScanUnavailable(true);
          }
        });
    }, 1000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [scanTs, fetchResults]);

  // Stop polling when scan is fully complete
  React.useEffect(() => {
    if (resultsData && resultsData.scan_complete && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [resultsData]);

  const handleStartScan = async () => {
    try {
      const result = await startScan().unwrap();
      setScanTs(result.scan_ts);
      setSelectedTaskIds(new Set());
      setAppliedTaskIds(new Set());
      setScanUnavailable(false);
      consecutiveErrorsRef.current = 0;
      toast.success('Scan started — polling for progress...');
    } catch {
      toast.error('Failed to start scan');
    }
  };

  const handleRefresh = () => {
    if (scanTs) {
      fetchResults({ scanTs });
    }
  };

  const handleApply = async () => {
    if (!scanTs || selectedTaskIds.size === 0) return;

    try {
      const result = await applyUpdates({
        scan_ts: scanTs,
        task_ids: Array.from(selectedTaskIds),
      }).unwrap();

      toast.success(
        `Applied ${result.applied} updates (${result.errors} errors)`,
      );

      // Mark applied tasks
      setAppliedTaskIds((prev) => {
        const next = new Set(prev);
        selectedTaskIds.forEach((id) => next.add(id));
        return next;
      });
      setSelectedTaskIds(new Set());
    } catch {
      toast.error('Failed to apply updates');
    }
  };

  // Filter helpers
  const toggleStatusFilter = (value: StatusFilterValue) => {
    setStatusFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        // Don't allow deselecting all
        if (next.size > 1) next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const toggleActionFilter = (value: ActionFilterValue) => {
    setActionFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        if (next.size > 1) next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  // Filtered results
  const filteredResults = React.useMemo(() => {
    if (!resultsData?.results) return [];
    return resultsData.results.filter((item) => {
      // Status filter
      if (!statusFilters.has(item.status)) return false;
      // Action filter — only apply to completed items that have an action
      if (item.status === 'completed' && item.action) {
        if (!actionFilters.has(item.action as ActionFilterValue)) return false;
      }
      return true;
    });
  }, [resultsData, statusFilters, actionFilters]);

  const visibleSelectableIds = React.useMemo(
    () =>
      new Set(
        filteredResults
          .filter((r) => r.status === 'completed' && !appliedTaskIds.has(r.task_id))
          .map((r) => r.task_id),
      ),
    [filteredResults, appliedTaskIds],
  );

  const allVisibleSelected =
    visibleSelectableIds.size > 0 &&
    [...visibleSelectableIds].every((id) => selectedTaskIds.has(id));

  const handleSelectAll = () => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      visibleSelectableIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleDeselectAll = () => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      visibleSelectableIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const toggleTaskSelection = (taskId: string) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const selectableCount = filteredResults.filter(
    (r) => r.status === 'completed' && !appliedTaskIds.has(r.task_id),
  ).length;

  const isFilterActive =
    statusFilters.size < ALL_STATUS_VALUES.length ||
    actionFilters.size < ALL_ACTION_VALUES.length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
          LOA Email Scan
        </h1>
        <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
          Scan all open LOR Acknowledgement tasks for existing email responses
          in Front. Fires off agentic LLMBlock workflows that search and read
          Front threads to determine if a follow-up email should be sent or
          stopped.
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Button
          color="blue"
          onClick={handleStartScan}
          disabled={isStarting}
          className="gap-1.5"
        >
          {isStarting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          {isStarting ? 'Starting...' : 'Start Scan'}
        </Button>

        {scanTs && (
          <>
            <Button
              outline
              onClick={handleRefresh}
              disabled={isFetchingResults}
              className="gap-1.5"
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetchingResults ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>

            <Button
              color="green"
              onClick={handleApply}
              disabled={isApplying || selectedTaskIds.size === 0}
              className="gap-1.5"
            >
              {isApplying ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Apply {selectedTaskIds.size} Update
              {selectedTaskIds.size !== 1 ? 's' : ''}
            </Button>

            {selectableCount > 0 && (
              <>
                <Button plain onClick={handleSelectAll}>
                  Select All Completed ({selectableCount})
                </Button>
                {selectedTaskIds.size > 0 && (
                  <Button plain onClick={handleDeselectAll}>
                    Deselect All
                  </Button>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Scan unavailable banner */}
      {scanUnavailable && (
        <div className="mb-6 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          This scan is no longer available. The workflow may have been cleaned
          up by the system. Start a new scan to get fresh results.
        </div>
      )}

      {/* Start Summary */}
      {resultsData && resultsData.start_phase_complete && (
        <div className="mb-6 rounded-lg border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-700 dark:bg-zinc-800/50">
          <div className="flex flex-wrap gap-4 text-sm">
            <span>
              <strong>Total tasks:</strong> {resultsData.total_tasks}
            </span>
            <span>
              <strong>Started:</strong> {resultsData.started}
            </span>
            <span>
              <strong>Skipped (running):</strong>{' '}
              {resultsData.skipped_running}
            </span>
            <span>
              <strong>Skipped (no matter):</strong>{' '}
              {resultsData.skipped_no_matter}
            </span>
            {(resultsData.start_errors ?? 0) > 0 && (
              <span className="text-red-600">
                <strong>Errors:</strong> {resultsData.start_errors}
              </span>
            )}
            <span className="text-zinc-500">
              scan_ts: {resultsData.scan_ts}
            </span>
          </div>
        </div>
      )}

      {/* Preparing indicator — workflow started but start phase not yet complete */}
      {scanTs && resultsData && !scanUnavailable && !resultsData.scan_unavailable && !resultsData.start_phase_complete && (
        <div className="mb-6 flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Preparing scan — gathering eligible tasks...
        </div>
      )}

      {/* Progress Bar */}
      {resultsData && !scanUnavailable && !resultsData.scan_unavailable && !resultsData.scan_complete && (resultsData.still_running ?? 0) > 0 && (
        <div className="mb-6">
          <div className="mb-1 flex items-center justify-between text-sm text-zinc-600 dark:text-zinc-400">
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Scanning...
            </span>
            <span>
              {(resultsData.completed ?? 0) + (resultsData.failed ?? 0)} / {resultsData.started ?? 0} completed
              ({resultsData.still_running ?? 0} running, {resultsData.failed ?? 0} failed)
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-zinc-200 dark:bg-zinc-700">
            <div
              className="h-2 rounded-full bg-blue-500 transition-all duration-500"
              style={{
                width: `${(resultsData.started ?? 0) > 0 ? (((resultsData.completed ?? 0) + (resultsData.failed ?? 0)) / (resultsData.started ?? 1)) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Completed indicator */}
      {resultsData &&
        resultsData.scan_complete &&
        (resultsData.results?.length ?? 0) > 0 && (
          <div className="mb-6 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" />
            Scan complete: {resultsData.completed ?? 0} completed,{' '}
            {resultsData.failed ?? 0} failed
          </div>
        )}

      {/* Filters */}
      {resultsData && (resultsData.results?.length ?? 0) > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-800/50">
          <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-zinc-500">
            <Filter className="h-3.5 w-3.5" />
            Filters
          </span>

          {/* Status filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Status:</span>
            {ALL_STATUS_VALUES.map((s) => (
              <button
                type="button"
                key={s}
                onClick={() => toggleStatusFilter(s)}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                  statusFilters.has(s)
                    ? STATUS_ACTIVE_CLASSES[s]
                    : INACTIVE_CLASS
                }`}
              >
                {s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())}
              </button>
            ))}
          </div>

          {/* Action filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">Action:</span>
            {ALL_ACTION_VALUES.map((a) => (
              <button
                type="button"
                key={a}
                onClick={() => toggleActionFilter(a)}
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium uppercase transition-colors ${
                  actionFilters.has(a)
                    ? STATUS_ACTIVE_CLASSES[a === 'send' ? 'completed' : 'failed']
                    : INACTIVE_CLASS
                }`}
              >
                {a}
              </button>
            ))}
          </div>

          {isFilterActive && (
            <span className="text-xs text-zinc-500">
              Showing {filteredResults.length} of {resultsData.results?.length ?? 0}
            </span>
          )}
        </div>
      )}

      {/* Results Table */}
      {resultsData && (resultsData.results?.length ?? 0) > 0 && (
        <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
          <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-700">
            <thead className="bg-zinc-50 dark:bg-zinc-800">
              <tr>
                <th className="w-10 px-3 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => {
                      if (allVisibleSelected) {
                        handleDeselectAll();
                      } else {
                        handleSelectAll();
                      }
                    }}
                    className="rounded border-zinc-300"
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Status
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Matter
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Carrier
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Age
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Action
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Confidence
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Tool Calls
                </th>
                <th className="min-w-[300px] px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Reason
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                  Evidence
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 bg-white dark:divide-zinc-700 dark:bg-zinc-900">
              {filteredResults.map((item) => {
                const isApplied = appliedTaskIds.has(item.task_id);
                const isSelected = selectedTaskIds.has(item.task_id);
                const canSelect =
                  item.status === 'completed' && !isApplied;

                return (
                  <tr
                    key={item.task_id}
                    className={`${isApplied ? 'bg-green-50 dark:bg-green-900/10' : ''} ${isSelected ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}
                  >
                    <td className="px-3 py-2">
                      {canSelect && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleTaskSelection(item.task_id)}
                          className="rounded border-zinc-300"
                        />
                      )}
                      {isApplied && (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <ScanStatusBadge status={item.status} />
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="font-medium text-zinc-900 dark:text-zinc-100">
                        {item.matter_name}
                      </div>
                      <Link
                        to={`/tasks/${item.task_id}`}
                        className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 hover:underline"
                      >
                        {item.task_id.slice(0, 8)}...
                        <ExternalLink className="h-3 w-3" />
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                      {item.carrier ?? '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                      {formatAge(item.task_created_at)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <ActionBadge action={item.action} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm">
                      {item.confidence !== null
                        ? `${(item.confidence * 100).toFixed(0)}%`
                        : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-zinc-500">
                      {item.tool_calls ?? '-'}
                    </td>
                    <td className="max-w-md px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                      {item.reason ?? '-'}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {item.evidence.length > 0 ? (
                        <div className="flex flex-col gap-1">
                          {item.evidence.map((ev, i) => {
                            const summary =
                              typeof ev.summary === 'string'
                                ? ev.summary
                                : '';
                            const conversationId =
                              typeof ev.conversation_id === 'string'
                                ? ev.conversation_id
                                : null;
                            return (
                              <div
                                key={i}
                                className="text-xs text-zinc-500"
                              >
                                {summary}
                                {conversationId && (
                                  <a
                                    href={`https://app.frontapp.com/open/${conversationId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-1 text-blue-500 hover:underline"
                                  >
                                    [Front]
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty state */}
      {!scanTs && !isStarting && (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-zinc-300 py-16 dark:border-zinc-600">
          <AlertTriangle className="mb-3 h-8 w-8 text-zinc-400" />
          <p className="text-sm text-zinc-500">
            Click &quot;Start Scan&quot; to begin scanning all open LOR
            Acknowledgement tasks for existing email responses in Front.
          </p>
        </div>
      )}
    </div>
  );
};

export default LOAScanPage;
